import React from 'react'
import clsx from 'clsx'
import { Popover, useTheme } from '@mui/material'
import { Filter } from 'iconsax-react'
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux'

import {
  filterToSelected,
  ITEMS,
  ITEM_TYPES,
  selectedToFilter
} from './helpers'
import { addView, selectViews } from '../../../../../slices/views'

import Button from '../../../../atoms/Button2'
import FilterModal from '../FilterModal'

import { isConversationInFilter } from '../../../../../slices/convo/helpers'
import { selectConvo, setView } from '../../../../../slices/convo'
import { useEnvironment } from '../../../../../providers/EnvironmentProvider'
import useStyles from './styles'

export * from './helpers'

type FilterMenuProps = {
  anchor: HTMLDivElement
}

const FilterMenu: React.FC<FilterMenuProps> = ({ anchor }) => {
  const dispatch = useAppDispatch()

  const { inbox, allConversations, topFilter, view } =
    useAppSelector(selectConvo)
  const { user } = useEnvironment()
  const { views } = useAppSelector(selectViews)

  const theme = useTheme()

  const [menuOpen, setMenuOpen] = React.useState(false)
  const [modalOpen, setModalOpen] = React.useState(false)
  const [selected, setSelected] = React.useState([])
  const hasSelections = React.useMemo(
    () => selected.length > 0,
    [selected.length]
  )
  const selectedView = React.useMemo(() => {
    if (view) {
      const selected = views.find(v => v.id === view)
      return selected
    }

    return null
  }, [view, views])

  // Calculate result count changes
  const resultCount = React.useMemo(() => {
    const filter = selectedToFilter(selected)
    const view = { filter } as APIView

    let count = 0
    Object.values(allConversations).forEach(session => {
      if (isConversationInFilter(session, topFilter, inbox, user?.id, view)) {
        count++
      }
    })

    return count
  }, [selected, allConversations, topFilter, inbox, user])

  const classes = useStyles({
    width: anchor?.offsetWidth ? anchor.offsetWidth - 48 : 0
  })

  React.useEffect(() => {
    if (!selectedView) {
      setSelected([])
      return
    }

    const asSelected = filterToSelected(selectedView.filter)
    setSelected(asSelected)
  }, [selectedView])

  const handleOpen = React.useCallback(e => {
    e.stopPropagation()
    setMenuOpen(true)
  }, [])

  const handleClose = React.useCallback(e => {
    e.stopPropagation()
    setMenuOpen(false)
    setModalOpen(false)
    setSelected([])
  }, [])

  const handleCloseModal = React.useCallback(e => {
    e.stopPropagation()
    setModalOpen(false)
  }, [])

  const handleSave = React.useCallback(
    async (e: any, name: string, temp = false) => {
      const filter = selectedToFilter(selected)
      const view: ViewUpdate = {
        filter,
        name,
        type: 'inbox',
        item_id: inbox,
        user_id: null,
        role_id: null
      }

      dispatch(
        addView(view, temp, newView => {
          dispatch(setView(newView.id))
          handleClose(e)
        })
      )
    },
    [selected, inbox, handleClose, dispatch]
  )

  const handleSelect = React.useCallback((itemID, typeInfo) => {
    setSelected(old => {
      // Remove from selected
      if (old.includes(itemID)) {
        return old.filter(id => id !== itemID)
      }

      // Allows multiple of this type
      if (typeInfo.multi) {
        return [...old, itemID]
      }

      // Remove others of this type and then add it
      const type = itemID.split('-')[0]
      const typeRemoved = old.filter(id => id.split('-')[0] !== type)
      return [...typeRemoved, itemID]
    })
  }, [])

  return (
    <>
      <Filter
        onClick={handleOpen}
        size='24'
        color={menuOpen ? theme.palette.primary.main : 'black'}
        variant='Bold'
        className={classes.root}
      />

      {menuOpen && (
        <Popover
          anchorEl={anchor}
          onClose={handleClose}
          transitionDuration={0}
          keepMounted
          open
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          classes={{ paper: classes.menu }}
        >
          <div className={classes.titleRow}>
            <div className={classes.title}>Filter</div>

            {hasSelections && (
              <div onClick={() => setSelected([])} className={classes.reset}>
                Reset
              </div>
            )}
          </div>

          <div className={classes.sections}>
            {Object.entries(ITEM_TYPES).map(([type, typeInfo]) => (
              <div key={type} className={classes.section}>
                <div className={classes.sectionTitle}>{type}</div>

                <div className={classes.sectionContent}>
                  {ITEMS.filter(item => item.type === type).map(item => (
                    <div
                      key={item.id}
                      onClick={() => handleSelect(item.id, typeInfo)}
                      className={clsx(
                        classes.item,
                        selected.includes(item.id) && classes.itemSelected
                      )}
                    >
                      {item.content}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {hasSelections && (
            <div className={classes.buttons}>
              <Button
                onClick={e => handleSave(e, 'temp', true)}
                className={classes.button}
              >
                Show {resultCount} Results
              </Button>

              <Button
                onClick={() => setModalOpen(true)}
                className={clsx(classes.button, classes.buttonInvert)}
              >
                Save and Show Results
              </Button>
            </div>
          )}
        </Popover>
      )}

      {modalOpen && (
        <FilterModal
          selected={selected}
          onClose={handleCloseModal}
          onSave={handleSave}
        />
      )}
    </>
  )
}

export default FilterMenu
